<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>Abonnements souscrits et moyens de paiement enregistrés pour la compagnie.</span>
      </div>
      <div class="ov-header__actions">
        <select
          v-if="isAdminSocieteMere"
          v-model="compagnieTransportRaisonSociale"
          class="ov-select sm-company"
          aria-label="Compagnie"
          @change="loadAll"
        >
          <option :value="null" disabled>Sélectionnez une compagnie</option>
          <option v-for="c in compagnieTransportList" :key="c.raisonSociale" :value="c.raisonSociale">{{ c.raisonSociale }}</option>
        </select>
        <button type="button" class="ov-btn ov-btn--primary" @click="$router.push('/creerModeAbonnement')">
          <v-icon small>mdi-plus</v-icon>
          Nouvel abonnement
        </button>
      </div>
    </div>

    <div v-if="isAdminSocieteMere && !compagnieTransportRaisonSociale" class="ov-card">
      <div class="ov-card__body">
        <div class="ov-empty">Sélectionnez une compagnie pour afficher ses abonnements et ses moyens de paiement.</div>
      </div>
    </div>

    <template v-else>
      <!-- ===== ABONNEMENTS ===== -->
      <div class="ov-card mb-6">
        <div class="ov-card__head">
          <div class="ov-card__title">
            Abonnements
            <span class="ov-count">{{ abonnementDisponibleList.length }}</span>
          </div>
          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input v-model="search" type="text" class="ov-input" placeholder="Rechercher un abonnement..." />
          </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="abonnementDisponibleList"
          :loading="loadingAbonnements"
          :search="search"
          loader-height="2"
          loading-text="Chargement des abonnements..."
          no-data-text="Aucun abonnement pour cette compagnie"
          class="ov-table"
        >
          <template v-slot:[`item.designation`]="{ item }">
            <span class="sm-strong">{{ item.designation }}</span>
          </template>
          <template v-slot:[`item.typeModeAbonnementDesignation`]="{ item }">
            <span class="ov-badge ov-badge--navy ov-badge--plain">{{ typeLabel(item.typeModeAbonnementDesignation) }}</span>
          </template>
          <template v-slot:[`item.conditions`]="{ item }">
            <span class="sm-conditions">{{ conditions(item) }}</span>
          </template>
          <template v-slot:[`item.dateDebutAbonnement`]="{ item }">
            <span class="sm-period">{{ item.dateDebutAbonnement || '—' }} → {{ item.dateFinAbonnement || '—' }}</span>
          </template>
          <template v-slot:[`item.statut`]="{ item }">
            <span class="ov-badge" :class="'ov-badge--' + status(item).tone">{{ status(item).label }}</span>
          </template>
        </v-data-table>
      </div>

      <!-- ===== MOYENS DE PAIEMENT ===== -->
      <div class="ov-card">
        <div class="ov-card__head">
          <div class="ov-card__title">
            Moyens de paiement
            <span class="ov-count">{{ paiementDisponibleList.length }}</span>
          </div>
          <button type="button" class="ov-btn ov-btn--secondary ov-btn--sm" @click="$router.push('/PaymentToolList')">
            Gérer les moyens de paiement
            <v-icon small>mdi-arrow-right</v-icon>
          </button>
        </div>

        <v-data-table
          :headers="paymentHeaders"
          :items="paiementDisponibleList"
          :loading="loadingPaiements"
          loader-height="2"
          hide-default-footer
          :items-per-page="-1"
          loading-text="Chargement des moyens de paiement..."
          no-data-text="Aucun moyen de paiement enregistré"
          class="ov-table"
        >
          <template v-slot:[`item.typeModePaiementDesignation`]="{ item }">
            <span class="ov-badge ov-badge--neutral ov-badge--plain">{{ item.typeModePaiementDesignation || '—' }}</span>
          </template>
          <template v-slot:[`item.designation`]="{ item }">
            <span class="sm-strong">{{ item.designation }}</span>
          </template>
          <template v-slot:[`item.telephoneGenerique`]="{ item }">
            <span>{{ item.telephoneGenerique ? '+225 ' + item.telephoneGenerique : '—' }}</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="ov-row-actions sm-actions">
              <button type="button" class="ov-icon-btn" title="Modifier" @click="editerModePaiement(item)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </button>
            </div>
          </template>
        </v-data-table>
      </div>
    </template>

    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_OBTENIR_MODE_ABONNEMENT_PAR_COMPAGNIE,
  API_OBTENIR_MODE_PAIEMENT_PAR_COMPAGNIE,
  API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
  HEADERS,
} from "../globalConfig/globalConstConfig";
import { showErrorMessage } from "@/modules/messages/messageProcess";

// "JJ/MM/AAAA" -> Date (null si le format est inattendu)
const parseDate = (value) => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(value || "");
  return match ? new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])) : null;
};

export default {
  name: "SelectionnerModeAbonnementEtPaiement",

  data() {
    return {
      errorMsg: null,
      loadingAbonnements: false,
      loadingPaiements: false,
      search: "",

      // Admin société mère : compagnie choisie ; autres rôles : compagnie de l'utilisateur
      compagnieTransportRaisonSociale: null,
      compagnieTransportList: [],

      headers: [
        { text: "Désignation", value: "designation" },
        { text: "Type", value: "typeModeAbonnementDesignation" },
        { text: "Conditions", value: "conditions", sortable: false },
        { text: "Période", value: "dateDebutAbonnement" },
        { text: "Statut", value: "statut", sortable: false },
      ],

      paymentHeaders: [
        { text: "Type", value: "typeModePaiementDesignation" },
        { text: "Désignation", value: "designation" },
        { text: "Téléphone", value: "telephoneGenerique" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],

      abonnementDisponibleList: [],
      paiementDisponibleList: [],
    };
  },

  computed: {
    isAdminSocieteMere() {
      return this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere";
    },

    targetCompany() {
      return this.isAdminSocieteMere
        ? this.compagnieTransportRaisonSociale
        : this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
    },
  },

  methods: {
    typeLabel(designation) {
      return (
        { AbonnementPeriodique: "Périodique", AbonnementPrelevement: "Prélèvement" }[designation] || designation || "—"
      );
    },

    conditions(item) {
      const fmt = (v) => Number(v).toLocaleString("fr-FR");
      if (item.taux !== null && item.taux !== undefined) {
        return `${item.taux} % des ventes` + (item.periodiciteAbonnementDesignation ? ` · ${item.periodiciteAbonnementDesignation}` : "");
      }
      if (item.redevance !== null && item.redevance !== undefined) {
        const pub = item.redevancePublicite ? ` + ${fmt(item.redevancePublicite)} pub.` : "";
        return `${fmt(item.redevance)} FCFA${pub}` + (item.periodiciteAbonnementDesignation ? ` · ${item.periodiciteAbonnementDesignation}` : "");
      }
      return item.periodiciteAbonnementDesignation || "—";
    },

    // Statut déduit des dates de l'abonnement
    status(item) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const debut = parseDate(item.dateDebutAbonnement);
      const fin = parseDate(item.dateFinAbonnement);
      if (fin && fin < today) return { label: "Terminé", tone: "neutral" };
      if (debut && debut > today) return { label: "À venir", tone: "navy" };
      if (debut || fin) return { label: "En cours", tone: "success" };
      return { label: "—", tone: "neutral" };
    },

    showError(message) {
      this.errorMsg = message;
      showErrorMessage();
    },

    async fetchList(url) {
      try {
        const response = await axios.post(
          url,
          { data: { compagnieTransportRaisonSociale: this.targetCompany } },
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        );
        if (response.status == 200 && response.data.status.code == 800) {
          return response.data.items || [];
        }
        this.showError((response.data && response.data.status && response.data.status.message) || "Erreur lors du chargement.");
      } catch (e) {
        this.showError((e && e.message) || "Erreur réseau lors du chargement.");
      }
      return [];
    },

    // RECUPERER LA LISTE DES MODES D'ABONNEMENTS ENREGISTRÉS PAR COMPAGNIE DE TRANSPORT
    async obtenirAbondemmentListParCompagnieTransport() {
      this.loadingAbonnements = true;
      this.abonnementDisponibleList = await this.fetchList(API_OBTENIR_MODE_ABONNEMENT_PAR_COMPAGNIE);
      this.loadingAbonnements = false;
    },

    // RECUPERER LA LISTE DES MODES DE PAIEMENT DISPONIBLE PAR COMPAGNIE DE TRANSPORT
    async obtenirPaiementListParCompagnieTransport() {
      this.loadingPaiements = true;
      this.paiementDisponibleList = await this.fetchList(API_OBTENIR_MODE_PAIEMENT_PAR_COMPAGNIE);
      this.loadingPaiements = false;
    },

    loadAll() {
      if (!this.targetCompany) return;
      this.obtenirAbondemmentListParCompagnieTransport();
      this.obtenirPaiementListParCompagnieTransport();
    },

    // LISTE DES COMPAGNIES (ADMIN SOCIÉTÉ MÈRE)
    async getCompagnieTransportList() {
      try {
        const response = await axios.post(
          API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
          { index: 0, size: 100 },
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        );
        if (response.status == 200 && response.data.status.code == 800) {
          this.compagnieTransportList = response.data.items || [];
        } else {
          this.showError((response.data && response.data.status && response.data.status.message) || "Impossible de charger les compagnies.");
        }
      } catch (e) {
        this.showError((e && e.message) || "Impossible de charger les compagnies.");
      }
    },

    //EDITER UN MODE DE PAIEMENT
    editerModePaiement(modePaiement) {
      localStorage.setItem("modePaiement", JSON.stringify(modePaiement));
      this.$router.push({ path: "/modifierModePaiement" });
    },
  },

  mounted() {
    if (this.isAdminSocieteMere) {
      this.getCompagnieTransportList();
    } else {
      this.loadAll();
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

.sm-company {
  width: auto;
  min-width: 240px;
  height: 42px;
}

.sm-strong {
  font-weight: 700;
  color: $ov-navy;
}

.sm-conditions,
.sm-period {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: $ov-ink;
}

.sm-actions {
  justify-content: flex-end;
}
</style>
